import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import useCan from 'hooks/useCan';
import { CAPABILITIES } from 'utils/capabilities';
import './FeatureGate.css';

/**
 * Shown in place of a feature the store's plan does not include.
 *
 * The copy is generic on purpose. Which tier unlocks a feature is plan data that
 * changes without a deploy, so promising a specific tier here would go stale.
 *
 * The call to action only appears for users who can actually act on it (the
 * roles granted `subscription.manage`). Hosts and view-only teammates see the
 * same gate on shared surfaces such as the demo summary, but pointing them at
 * a plans page they cannot act on would be worse than saying nothing.
 *
 * @param {String} message what is unavailable
 * @param {Boolean} showAction force the action link off, regardless of role
 * @param {String} variant 'inline' for a single line, 'block' for a panel
 * @returns {ReactComponent} the upgrade prompt
 */

const UpgradePrompt = ({
  message = 'This feature is not included in your current plan.',
  showAction = true,
  variant = 'inline',
}) => {
  const { allowed: canManagePlan } = useCan(CAPABILITIES.SUBSCRIPTION_MANAGE);

  return (
    <div className={`upgrade-prompt upgrade-prompt-${variant}`}>
      <span className="upgrade-prompt-icon" aria-hidden="true">
        🔒
      </span>
      <span className="upgrade-prompt-message">{message}</span>
      {showAction && canManagePlan && (
        <Link className="upgrade-prompt-link" to="/user/subscription">
          Upgrade your plan
        </Link>
      )}
    </div>
  );
};

UpgradePrompt.propTypes = {
  message: PropTypes.string,
  showAction: PropTypes.bool,
  variant: PropTypes.oneOf(['inline', 'block']),
};

export default UpgradePrompt;
